import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircleNotch } from 'phosphor-react';
import Layout from '../../../components/Learner/Layout';
import { useDashboardData } from '../../../hooks/useDashboardData';
import GreetingBanner from '../../../components/Learner/Dashboard/GreetingBanner';
import ReportCard from '../../../components/Learner/Dashboard/ReportCard';
import RombelTable from '../../../components/Learner/Dashboard/RombelTable';

const LearnerDashboard = () => {
  const { loading, data, error, subcourses } = useDashboardData();
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.log('❌ No token found, redirecting to login...');
      navigate('/learner/login');
    }
  }, [navigate]);

  if (loading) {
    return (
      <Layout>
        <div className="max-w-full mx-auto p-6 md:p-6 overflow-x-hidden flex items-center justify-center min-h-[60vh] py-12 md:py-12 px-4 md:px-6">
          <div className="flex flex-col items-center justify-center gap-3 p-6 text-[var(--text-secondary)]">
            <CircleNotch size={24} weight="bold" className="animate-spin" />
            <span className="text-[14px] text-[var(--text-secondary)]">Loading dashboard...</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="max-w-full mx-auto p-0 md:p-6 overflow-x-hidden pb-[100px] md:pb-6 w-full">
          <div className="bg-[var(--card-bg)] p-6 md:p-12 rounded-none md:rounded-xl shadow-[var(--shadow-sm)] text-center mx-0 md:mx-0">
            <p className="text-base text-[var(--error-color)] m-0">{error}</p>
          </div>
        </div>
      </Layout>
    );
  }

  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const phaseName = data?.data?.phase?.name || 'No Phase';

  // changed by Cursor: only show levels that user has practiced
  // Create a map of subcourses by level for quick lookup
  // Handle various name formats: "A1", "Lesen A1", "Schreiben A2", etc.
  const subcoursesMap = new Map();
  subcourses.forEach(subcourse => {
    const name = (subcourse.name || '').trim();
    // Try to extract level from name
    // Format 1: Direct match "A1", "A2", "B1", "B2"
    // Format 2: Contains level "Lesen A1", "Schreiben A2", etc.
    const levelMatch = name.match(/\b(A1|A2|B1|B2)\b/i);
    if (levelMatch) {
      const level = levelMatch[1].toUpperCase();
      // If level already exists, prefer the one with more complete data
      // Priority: has user-stats > has all-stats > has higher progress > first one
      const existing = subcoursesMap.get(level);
      if (!existing) {
        subcoursesMap.set(level, subcourse);
      } else {
        const existingStats = existing['user-stats']?.['all-stats'];
        const currentStats = subcourse['user-stats']?.['all-stats'];
        const existingProgress = existingStats?.progress || 0;
        const currentProgress = currentStats?.progress || 0;
        
        // Prefer subcourse with user-stats if existing doesn't have it
        if (!existing['user-stats'] && subcourse['user-stats']) {
          subcoursesMap.set(level, subcourse);
        }
        // If both have stats, prefer the one with higher progress (more activity)
        else if (existing['user-stats'] && subcourse['user-stats'] && currentProgress > existingProgress) {
          subcoursesMap.set(level, subcourse);
        }
        // If same progress, prefer the one with exact level name match (e.g., "A1" over "Lesen A1")
        else if (existing['user-stats'] && subcourse['user-stats'] && currentProgress === existingProgress) {
          const existingNameUpper = (existing.name || '').toUpperCase();
          const currentNameUpper = name.toUpperCase();
          if (currentNameUpper === level && existingNameUpper !== level) {
            subcoursesMap.set(level, subcourse);
          }
        }
      }
    } else {
      // Fallback: check if name itself is a level
      const upperName = name.toUpperCase();
      if (['A1', 'A2', 'B1', 'B2'].includes(upperName)) {
        const existing = subcoursesMap.get(upperName);
        if (!existing || (!existing['user-stats'] && subcourse['user-stats'])) {
          subcoursesMap.set(upperName, subcourse);
        }
      }
    }
  });

  // Only show levels that user has practiced (exist in subcoursesMap)
  const displaySubcourses = Array.from(subcoursesMap.values());

  return (
    <Layout>
      <div className="max-w-full mx-auto p-0 md:p-6 overflow-x-hidden pb-[100px] md:pb-6 w-full">
        <GreetingBanner 
          userName={userData.name} 
          phaseName={phaseName}
        />

        {/* Subcourses Progress */}
        {/* <div className="dashboard-subcourses">
          <h2 className="subcourses-title">Your Progress</h2>
          <div className="subcourses-grid">
            {displaySubcourses.map((subcourse) => (
              <ReportCard key={subcourse._id} subcourse={subcourse} />
            ))}
          </div>
        </div> */}

        {/* Rombel Table */}
        <RombelTable />
      </div>
    </Layout>
  );
};

export default LearnerDashboard;

